import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';
import { useApiClient } from '../api/client.jsx';
import { useAuth } from '../auth/AuthProvider';
import { useBrand } from '../brand/BrandProvider';
import { createDebugLogger, createErrorLogger } from '../utils/debug';

const dlog = createDebugLogger('login');
const elog = createErrorLogger('login');
import { isApiError } from '../api/errors';
import { extractTokens } from '../auth/extractTokens';

export function LoginPage() {
  const { login } = useApiClient();
  const auth = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [brandInput, setBrandInput] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const emailRef = useRef(null);

  // a11y-konformer Initial-Focus (programmatisch statt `autoFocus`-Prop)
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const from = location.state?.from?.pathname ?? '/';

  // Vermeidet dass ein authenticated User auf der Login-Page hängenbleibt
  // (z. B. wenn er Bookmark / Back-Button benutzt). RootRedirect übernimmt.
  if (auth.status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  // Per security-review MEDIUM-1: Login darf nicht ohne explizite Brand-
  // Origin starten. In Prod ist der Hardcoded-Fallback weg → Brand-Picker
  // wird zwingend vor jedes Formular gehängt.
  if (!brand.currentBrandOrigin) {
    return (
      <BrandPickerGate
        value={brandInput}
        onChange={setBrandInput}
        onConfirm={() => brand.setCurrentBrandOrigin(brandInput.trim())}
      />
    );
  }

  // Zitadel-Mode: kein lokales Formular, Redirect-Button zur Hosted-Login-UI.
  if (auth.mode === 'zitadel') {
    return <ZitadelLoginPanel auth={auth} from={from} />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await login(email, password);
      dlog('response', res);
      if (res?.mfaRequired) {
        auth.beginMfa(res.mfaChallenge);
        navigate('/mfa-challenge', { replace: true });
        return;
      }
      const tokens = extractTokens(res, 'login-response');
      auth.setTokens(tokens);
      navigate(from, { replace: true });
    } catch (err) {
      elog('error', err);
      setError(isApiError(err) ? err.message : (err?.message ?? 'Login fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-body-tertiary">
      <Card style={{ width: 380 }}>
        <CardBody>
          <CardTitle tag="h4" className="mb-4">
            🛰️ Orbit Login
          </CardTitle>
          <Form onSubmit={onSubmit}>
            <FormGroup>
              <Label for="email">E-Mail</Label>
              <Input
                innerRef={emailRef}
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </FormGroup>
            {error && <Alert color="danger">{error}</Alert>}
            <Button color="primary" type="submit" block disabled={busy}>
              {busy ? 'Anmelden…' : 'Anmelden'}
            </Button>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}

function BrandPickerGate({ value, onChange, onConfirm }) {
  const onSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    onConfirm();
  };
  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-body-tertiary">
      <Card style={{ width: 420 }}>
        <CardBody>
          <CardTitle tag="h4" className="mb-3">🛰️ Orbit</CardTitle>
          <p className="text-muted small mb-3">
            Wähle die Brand für den Login. Core erwartet den
            <code> Brand-Origin </code>-Header bereits beim Auth-Call.
            Ein versehentlich falsch gewählter Origin würde gegen die
            falsche Brand authentifizieren.
          </p>
          <Form onSubmit={onSubmit}>
            <FormGroup>
              <Label for="brand-origin">Brand-Origin</Label>
              <Input
                id="brand-origin"
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="z.B. valikey.app"
                required
              />
            </FormGroup>
            <Button color="primary" type="submit" block disabled={!value.trim()}>
              Weiter zum Login
            </Button>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}

function ZitadelLoginPanel({ auth, from }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await auth.beginZitadelLogin(from);
      // Redirect erfolgt — Funktion kehrt nicht zurück
    } catch (err) {
      setBusy(false);
      setError(err?.message ?? 'Zitadel-Login konnte nicht gestartet werden.');
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-body-tertiary">
      <Card style={{ width: 420 }}>
        <CardBody className="text-center">
          <CardTitle tag="h4" className="mb-3">🛰️ Orbit</CardTitle>
          <p className="text-muted small mb-4">
            Mitarbeiter-Anmeldung über die zentrale Identity-Verwaltung.
            Du wirst zur Login-Seite weitergeleitet.
          </p>
          {error && <Alert color="danger" className="text-start">{error}</Alert>}
          <Button color="primary" size="lg" block onClick={onLogin} disabled={busy}>
            {busy ? 'Weiterleiten…' : 'Mit Zitadel anmelden'}
          </Button>
          <div className="small text-muted mt-3">
            Endkunden bitte über die jeweilige Brand-Login-Seite anmelden.
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
